import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
    AppBar, Avatar, Box, Button, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader,
    Toolbar, Tooltip, Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ApiService from '../network/API';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import ThemeToggle from './ThemeToggle';

const WIDTH = 240;
const WIDTH_COLLAPSED = 68;
const PREF_KEY = 'sidebar_collapsed';

const ADMIN_ITEMS = [
    {label: 'Группы', to: '/groups', icon: <GroupsOutlinedIcon/>},
    {label: 'Расписание', to: '/admin/schedule', icon: <CalendarMonthOutlinedIcon/>},
    {label: 'АСУ РСО', to: '/admin/asurso', icon: <FactCheckOutlinedIcon/>},
    {label: 'Лаборатории', to: '/admin/labs', icon: <ScienceOutlinedIcon/>},
    {label: 'Обращения', to: '/admin/appeals', icon: <MarkChatUnreadOutlinedIcon/>},
    {label: 'Git-репозитории', to: '/admin/git-tasks', icon: <AccountTreeOutlinedIcon/>},
    {label: 'Экзамены', to: '/admin/exams', icon: <QuizOutlinedIcon/>},
    {label: 'Курсовые', to: '/admin/courseworks', icon: <SchoolOutlinedIcon/>},
    {label: 'MySQL-базы', to: '/admin/mysql-tasks', icon: <StorageOutlinedIcon/>},
    {label: 'AI-расход', to: '/admin/llm', icon: <AutoAwesomeOutlinedIcon/>},
    {label: 'Мессенджеры', to: '/admin/messengers', icon: <ForumOutlinedIcon/>},
];

const readPref = () => {
    try {
        const v = localStorage.getItem(PREF_KEY);
        return v === 'true' ? true : v === 'false' ? false : null;
    } catch (e) {
        return null;
    }
};

const writePref = (value) => {
    try {
        localStorage.setItem(PREF_KEY, String(value));
    } catch (e) {
        // не критично
    }
};

const isSelected = (pathname, to) => pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));

const NavItem = ({icon, label, to, onClick, collapsed, selected}) => {
    const button = (
        <ListItemButton
            component={to ? Link : 'div'}
            to={to}
            selected={selected}
            onClick={onClick}
            sx={{
                borderRadius: 2, mx: 1, mb: 0.25, minHeight: 40, px: collapsed ? 0 : 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
            }}
        >
            <ListItemIcon sx={{minWidth: collapsed ? 0 : 36, justifyContent: 'center', color: selected ? 'primary.main' : 'inherit'}}>{icon}</ListItemIcon>
            {!collapsed && <ListItemText primary={label} primaryTypographyProps={{noWrap: true, fontSize: '0.9rem', fontWeight: selected ? 700 : 500}}/>}
        </ListItemButton>
    );
    return collapsed ? <Tooltip title={label} placement="right">{button}</Tooltip> : button;
};

// Содержимое панели: логотип, навигация, разделы, управление (админ) и блок пользователя.
const SidebarContent = ({collapsed, onToggleCollapse, showToggle, onNavigate, mode, toggleTheme, user, tags, admin, pathname}) => {
    const navigate = useNavigate();
    const nav = (props) => <NavItem collapsed={collapsed} onClick={onNavigate} {...props} selected={isSelected(pathname, props.to)}/>;

    const handleLogout = () => {
        onNavigate && onNavigate();
        ApiService.logout();
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', overflowX: 'hidden'}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', px: collapsed ? 0 : 2, py: 1.5, minHeight: 60}}>
                <Box component={Link} to="/" onClick={onNavigate} sx={{display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit'}}>
                    <CodeIcon color="primary"/>
                    {!collapsed && <Typography variant="h6" fontWeight={800} letterSpacing="-0.03em" noWrap>danbel.ru</Typography>}
                </Box>
                {showToggle && !collapsed && (
                    <IconButton size="small" onClick={onToggleCollapse} aria-label="Свернуть панель"><ChevronLeftIcon/></IconButton>
                )}
            </Box>
            {showToggle && collapsed && (
                <Tooltip title="Развернуть панель" placement="right">
                    <IconButton size="small" onClick={onToggleCollapse} sx={{alignSelf: 'center', mb: 1}} aria-label="Развернуть панель"><ChevronRightIcon/></IconButton>
                </Tooltip>
            )}

            <Box sx={{flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 1}}>
                <List dense disablePadding>
                    {nav({label: 'Лекции', to: '/articles', icon: <MenuBookOutlinedIcon/>})}
                    {user && !admin && nav({label: 'Расписание', to: '/schedule', icon: <CalendarMonthOutlinedIcon/>})}
                    {user && !admin && nav({label: 'Экзамены', to: '/exams', icon: <AssignmentOutlinedIcon/>})}
                    {user && !admin && nav({label: 'Обращения', to: '/appeals', icon: <MarkChatUnreadOutlinedIcon/>})}
                    {user && nav({label: 'Уведомления', to: '/notifications', icon: <NotificationsNoneIcon/>})}
                    {user && nav({
                        label: user.username || 'Профиль', to: '/profile',
                        icon: user.avatarFileName
                            ? <Avatar src={ApiService.getFileUrl(user.avatarFileName)} sx={{width: 24, height: 24}}/>
                            : <PersonOutlineIcon/>,
                    })}
                </List>

                {tags.length > 0 && (
                    <List dense disablePadding subheader={collapsed ? undefined : (
                        <ListSubheader disableSticky sx={{bgcolor: 'transparent', lineHeight: '32px', mt: 1, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase'}}>
                            Разделы
                        </ListSubheader>
                    )}>
                        {collapsed ? (
                            <NavItem collapsed icon={<FolderOutlinedIcon/>} label="Разделы" onClick={onToggleCollapse || (() => navigate('/articles'))}/>
                        ) : tags.map((tag) => nav({key: tag.id, label: tag.name, to: `/tags/${tag.id}`, icon: <FolderOutlinedIcon/>}))}
                    </List>
                )}

                {admin && (
                    <List dense disablePadding subheader={collapsed ? <Divider sx={{my: 1, mx: 2}}/> : (
                        <ListSubheader disableSticky sx={{bgcolor: 'transparent', lineHeight: '32px', mt: 1, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase'}}>
                            Управление
                        </ListSubheader>
                    )}>
                        {ADMIN_ITEMS.map((item) => nav({key: item.to, ...item}))}
                    </List>
                )}
            </Box>

            <Divider/>
            <Box sx={{p: 1}}>
                <Box sx={{display: 'flex', flexDirection: collapsed ? 'column' : 'row', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', gap: 0.5, px: 1, pt: 0.5}}>
                    <ThemeToggle mode={mode} onToggle={toggleTheme} compact={collapsed}/>
                    {user ? (
                        <Tooltip title="Выйти" placement="right">
                            <IconButton size="small" onClick={handleLogout}><LogoutIcon fontSize="small"/></IconButton>
                        </Tooltip>
                    ) : collapsed ? (
                        <Tooltip title="Войти" placement="right">
                            <IconButton size="small" onClick={() => navigate('/sign-in')}><LoginIcon fontSize="small"/></IconButton>
                        </Tooltip>
                    ) : (
                        <Button size="small" variant="contained" startIcon={<LoginIcon/>} onClick={() => { onNavigate && onNavigate(); navigate('/sign-in'); }}>Войти</Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// Боковая панель вместо верхнего меню: на компьютере постоянная (сворачивается в узкую полосу с иконками, на странице задачи
// свёрнута по умолчанию), на телефоне открывается из тонкой верхней полосы.
export const Sidebar = ({mode, toggleTheme, mobileOpen, setMobileOpen}) => {
    const location = useLocation();
    const [tags, setTags] = useState([]);
    const [user, setUser] = useState(null);
    const [pref, setPref] = useState(readPref);
    const [problemOverride, setProblemOverride] = useState(null);
    const tokenRef = useRef(null);

    const onProblemPage = location.pathname.startsWith('/problems/');
    // Вне страницы задачи действует сохранённый выбор, на странице задачи — свёрнута, пока студент не развернёт её вручную.
    const collapsed = onProblemPage ? (problemOverride ?? true) : (pref ?? false);

    useEffect(() => {
        if (!onProblemPage) setProblemOverride(null);
    }, [onProblemPage]);

    // Разделы и пользователь: при первом открытии и после входа/выхода (меняется токен).
    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (tokenRef.current === token && tokenRef.current !== null) return;
        tokenRef.current = token || '';
        ApiService.getAllTags().then((list) => setTags(Array.isArray(list) ? list : [])).catch(() => setTags([]));
        if (token) ApiService.getMeUser().then(setUser).catch(() => setUser(null));
        else setUser(null);
    }, [location.pathname]);

    const toggle = () => {
        if (onProblemPage) {
            setProblemOverride(!collapsed);
        } else {
            writePref(!collapsed);
            setPref(!collapsed);
        }
    };

    const admin = ApiService.isAdmin();
    const common = {mode, toggleTheme, user, tags, admin, pathname: location.pathname};
    const width = collapsed ? WIDTH_COLLAPSED : WIDTH;

    return (
        <>
            <Drawer
                variant="permanent"
                sx={{
                    display: {xs: 'none', md: 'block'},
                    width, flexShrink: 0, whiteSpace: 'nowrap',
                    transition: (t) => t.transitions.create('width', {duration: t.transitions.duration.shorter}),
                    '& .MuiDrawer-paper': {
                        width, boxSizing: 'border-box', overflowX: 'hidden',
                        transition: (t) => t.transitions.create('width', {duration: t.transitions.duration.shorter}),
                    },
                }}
            >
                <SidebarContent {...common} collapsed={collapsed} showToggle onToggleCollapse={toggle}/>
            </Drawer>

            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{keepMounted: true}}
                sx={{display: {xs: 'block', md: 'none'}, '& .MuiDrawer-paper': {width: WIDTH, boxSizing: 'border-box'}}}
            >
                <SidebarContent {...common} collapsed={false} showToggle={false} onNavigate={() => setMobileOpen(false)}/>
            </Drawer>
        </>
    );
};

// Тонкая верхняя полоса на телефоне: кнопка меню и название.
export const MobileTopBar = ({onMenu}) => (
    <AppBar position="sticky" elevation={1} sx={{display: {xs: 'block', md: 'none'}}}>
        <Toolbar variant="dense" sx={{minHeight: 48, px: 1}}>
            <IconButton color="inherit" edge="start" onClick={onMenu} aria-label="Открыть меню"><MenuIcon/></IconButton>
            <Box component={Link} to="/" sx={{display: 'flex', alignItems: 'center', gap: 0.75, ml: 1, textDecoration: 'none', color: 'inherit'}}>
                <CodeIcon fontSize="small"/>
                <Typography fontWeight={800} letterSpacing="-0.03em">danbel.ru</Typography>
            </Box>
        </Toolbar>
    </AppBar>
);
